import { Button } from "@mantine/core";
import { IconCirclePlus } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { IconButton } from "../../mantine/components/IconButton/IconButton";
import { useToast } from "../../mantine/notifications/useToast";
import { BasicConfirmationModal } from "../BuildingBlocks/Layout/Modals/BasicConfirmationModal/BasicConfirmationModal";
import type {
  ChangeableFieldErrors,
  ChangeableFieldTableOptions,
  ValueColumns
} from "./ChangeableFieldTable/ChangeableFieldTable";
import { ChangeableFieldTable } from "./ChangeableFieldTable/ChangeableFieldTable";
import "./ChangeableFieldWidget.scss";
import { addNewChangeableFieldValue } from "./utils/addNewChangeableFieldValue";
import { updateChangeableFieldValue } from "./utils/updateChangeableFieldValue";

export interface ChangeableFieldData<T = number> {
  startDate: DateTime | null;
  endDate: DateTime | null;
  value: T | null;
  id?: number;
}

export interface ChangeableFieldWidgetOptions extends ChangeableFieldTableOptions {
  addButtonText?: string;
  saveButtonText?: string;
  addNullStartDateWhenNoValues?: boolean;
  canAddFutureDates?: boolean;
  hideAddButtonAfterFirstEntry?: boolean;
  hideCancelButton?: boolean;
}

export interface ChangeableFieldWidgetProps<T> {
  initialValues: Array<ChangeableFieldData<T>>;
  dataName: string;
  options?: ChangeableFieldWidgetOptions;
  confirmationOnSaveElement?: React.ReactNode;
  confirmationOnSaveCancellable?: boolean;
  valueColumns?: ValueColumns<T>;
  onCancel: () => void;
  onDone: (newValues: Array<ChangeableFieldData<T | null>>) => Promise<void>;
  errors?: ChangeableFieldErrors;
}

/** Manages time-changeable fields of type T (default number)
 *
 * If T is specified, `valueColumns` must also be specified.
 */
function ChangeableFieldWidget<T>({
  initialValues,
  dataName,
  options = {},
  confirmationOnSaveElement,
  confirmationOnSaveCancellable = true,
  valueColumns,
  onCancel,
  onDone,
  errors
}: ChangeableFieldWidgetProps<T>) {
  const [values, setValues] =
    useState<Array<ChangeableFieldData<T>>>(initialValues);
  const [isSaving, setIsSaving] = useState(false);
  const [showBasicConfirmationModal, setShowBasicConfirmationModal] =
    useState(false);

  const { showErrorToast } = useToast();

  const isButtonHidden =
    values.length !== 0 && options.hideAddButtonAfterFirstEntry;

  function handleAddValue() {
    setValues((currentValues) =>
      addNewChangeableFieldValue(currentValues, options)
    );
  }

  const handleUpdateValue = useCallback(
    (index: number, updatedValue: ChangeableFieldData<T>) => {
      setValues((currentValues) =>
        updateChangeableFieldValue(updatedValue, index, currentValues)
      );
    },
    [setValues]
  );

  const handleDeleteValue = useCallback(
    (index: number) => {
      setValues((currentValues) => {
        const newValues = [...currentValues];
        const startDateOfDeleted = newValues[index].startDate;

        if (newValues[index + 1]) {
          newValues[index + 1] = {
            ...newValues[index + 1],
            startDate: startDateOfDeleted
          };
        } else if (index === newValues.length - 1 && index > 0) {
          newValues[index - 1] = {
            ...newValues[index - 1],
            endDate: null
          };
        }

        newValues.splice(index, 1);

        return newValues;
      });
    },
    [setValues]
  );

  function handleClickSave(alreadyConfirmed = false) {
    if (!!confirmationOnSaveElement && !alreadyConfirmed) {
      setShowBasicConfirmationModal(true);
      return;
    }

    setIsSaving(true);
    onDone(values)
      .then(() => {
        setShowBasicConfirmationModal(false);
      })
      .catch((error) => {
        showErrorToast({
          message:
            typeof error === "string"
              ? error
              : "Fehler beim Speichern der Änderungen."
        });
      })
      .finally(() => {
        setIsSaving(false);
      });
  }

  const yesterday = DateTime.now().plus({ days: -1 }).startOf("day");
  const lastStartDate =
    values.length > 0 ? values[values.length - 1].startDate : null;
  const canAddValue =
    !lastStartDate ||
    lastStartDate.startOf("day") < yesterday ||
    options.canAddFutureDates;
  const canSave = values.reduce((canSave, value, index) => {
    const isFirstValue = index === 0;
    const validStartDate =
      !!value.startDate || (isFirstValue && !value.startDate);
    const isLastValue = index === values.length - 1;
    const validEndDate = !!value.endDate || (isLastValue && !value.endDate);

    return canSave && validStartDate && validEndDate;
  }, true);

  const memoizedChangeableFieldTable = useMemo(
    () => (
      <ChangeableFieldTable
        dataName={dataName}
        errors={errors}
        tableOptions={options}
        valueColumns={valueColumns}
        values={values}
        onDeleteValue={handleDeleteValue}
        onUpdateValue={handleUpdateValue}
      />
    ),
    [
      dataName,
      errors,
      options,
      valueColumns,
      values,
      handleDeleteValue,
      handleUpdateValue
    ]
  );
  return (
    <div className="ChangeableFieldWidget">
      {memoizedChangeableFieldTable}
      {!isButtonHidden && (
        <IconButton
          color="primary"
          disabled={!canAddValue}
          icon={IconCirclePlus}
          mt="md"
          onClick={handleAddValue}
        >
          {options.addButtonText ?? "Eintragen"}
        </IconButton>
      )}
      <div className="save-cancel-buttons">
        <Button
          color="secondary"
          disabled={!canSave}
          loading={isSaving}
          onClick={() => handleClickSave()}
        >
          {!options.saveButtonText ? "Speichern" : options.saveButtonText}
        </Button>
        {!options.hideCancelButton && (
          <Button
            variant="default"
            onClick={() => {
              setValues(initialValues);
              onCancel();
            }}
          >
            Abbrechen
          </Button>
        )}
      </div>
      <BasicConfirmationModal
        opened={showBasicConfirmationModal}
        toggleModal={() => setShowBasicConfirmationModal(false)}
        onCancel={
          confirmationOnSaveCancellable ? () => setIsSaving(false) : undefined
        }
        onConfirm={() => handleClickSave(true)}
      >
        {confirmationOnSaveElement}
      </BasicConfirmationModal>
    </div>
  );
}

export { ChangeableFieldWidget };
